/**
 * Island furniture (ground vocabulary — an island top IS local ground):
 * stub sidings and boost fans on the ledge cells the planner claimed
 * beside island runs (IslandCrossing.sideCells).
 *
 * The planner claimed the ledge band and the island builder put full mass
 * beneath it; this pass runs AFTER realize/assemble because a ledge is
 * only furnishable when its host cell realized as a plain straight (a
 * tunnel arch or a wave has no side port). Stub hosts mutate in place
 * (straight → split, the loop-mode stubs.ts move: the junction's main
 * channel IS the host's channel; a right-hand branch renders the native
 * left-branching mesh mirrored); fans stand in the ledge cell with the
 * blade side facing the channel.
 */
import type { Dir, Placement } from "../../shared/types.ts";
import type { Rng } from "../rng.ts";
import type { RealizedWindow } from "../wfc.ts";
import { turnLeft, turnRight } from "../geom.ts";
import type { SegmentPlan } from "./path.ts";

/** No hole caps on islands: a drain spur over solid mass reads wrong. */
const CAPS = ["end-rounded", "end-square"];
const FANS = [
  "fan-standing-two",
  "fan-standing-four",
  "fan-two",
  "fan-four",
  "fan-simple-two",
  "fan-simple-four",
];
const FAN_BOOST = 6;

// M16 wind variety: fans are force-field volumes now (client fanField.ts),
// and two new placements exploit that. Both replace the legacy cross fan
// on a ledge via draws APPENDED after the legacy ones — every rng stream
// that existed before this milestone draws byte-identically.
const STANDING_FANS = ["fan-standing-two", "fan-standing-four"];
const TABLE_FANS = ["fan-two", "fan-four", "fan-simple-two", "fan-simple-four"];
/** Boost corridor: standing fan aligned WITH the channel on the straight
 * run, blowing along travel. Punctuation, not weather. */
const BOOST_FAN_CHANCE = 0.22;
/** Updraft column: table fan flat at the channel lip — lift assist for
 * marbles hugging that edge. */
const UPDRAFT_FAN_CHANCE = 0.2;
/** Lip offset toward the channel, TRACK units. Must stay < 0.5 so the
 * fan still rounds to its ledge cell (the ground-vocabulary test's own
 * "fans exist only as ledge furniture" rule). */
const LIP_OFFSET = 0.45;

// M19a wind vocabulary: two more ledge-fan behaviors, drawn from values
// APPENDED after the M16 draws — every stream that existed before this
// milestone still draws byte-identically (per-cell child streams; nothing
// drew after `variant`).
/** Gust wall: a legacy cross fan upgraded to a pulsed blockade — full
 * blocked-phase strength shoves the line against the far wall, the
 * off-phase releases it. The pack must time the window. */
const GUST_CHANCE = 0.4;
/** Vortex: table fan at the lip whose swirl column parks over the
 * channel — marbles S-wiggle around the eddy center. */
const VORTEX_CHANCE = 0.15;
const GUST_STRENGTH = 14;
const GUST_LENGTH = 5;
const GUST_RADIUS = 1.1;
const GUST_DUTY = 0.45;
const GUST_PERIOD_MIN = 2.4;
const GUST_PERIOD_SPAN = 1.2;
const VORTEX_STRENGTH = 7;
const VORTEX_RADIUS = 1.2;
const VORTEX_HEIGHT = 1.6;
/** Dir index → unit step (0=+x, 1=+z, 2=−x, 3=−z). */
const DIR_STEP: readonly (readonly [number, number])[] = [
  [1, 0],
  [0, 1],
  [-1, 0],
  [0, -1],
];

export interface FurnishedPiece {
  placement: Placement;
  /** Absolute plan-cell index of the host (for chapter stamping). */
  host: number;
}

/** One gust wall's mutable pulse, for the coordination post-pass. */
export interface GustEntry {
  wind: { pulse: { period: number; duty: number; phase: number } };
  host: number;
  side: string;
  isle: number;
}

/** Phase coordination over drawn gust walls — rng-FREE (a pure rewrite
 * of pulse phase/period; which ledges gust was already drawn):
 *  - BANK: ≥2 same-side gusts on one island whose hosts are ≤2 cells
 *    apart phase-lock into a travelling wave (phase_i = i·period/N along
 *    travel) sharing the first member's period.
 *  - OPPOSED PAIR: L and R gusts on the SAME host share a period and go
 *    anti-phase (offset period/2) — an oscillating slalom gate.
 * Pair coupling runs last so a paired bank member still opposes its
 * across-channel partner. */
export function coordinateGusts(gusts: readonly GustEntry[]): void {
  const byIsleSide = new Map<string, GustEntry[]>();
  for (const g of gusts) {
    const key = `${g.isle}/${g.side}`;
    const list = byIsleSide.get(key);
    if (list) list.push(g);
    else byIsleSide.set(key, [g]);
  }
  for (const list of byIsleSide.values()) {
    list.sort((a, b) => a.host - b.host);
    let run: GustEntry[] = [];
    const flush = () => {
      if (run.length >= 2) {
        const period = run[0].wind.pulse.period;
        for (let i = 0; i < run.length; i++) {
          run[i].wind.pulse.period = period;
          run[i].wind.pulse.phase = (i * period) / run.length;
        }
      }
      run = [];
    };
    for (const g of list) {
      // Run gap ≤4 (M24 re-tune): an island's ledge row reads as one
      // authored bank even with a stub between the fans; climate biases
      // spread gusts thinner than the M19a ≤2 law assumed.
      if (run.length > 0 && g.host - run[run.length - 1].host > 4) flush();
      run.push(g);
    }
    flush();
  }
  // Opposed pairs: same island, same host, both sides.
  const byHost = new Map<string, GustEntry[]>();
  for (const g of gusts) {
    const key = `${g.isle}/${g.host}`;
    const list = byHost.get(key);
    if (list) list.push(g);
    else byHost.set(key, [g]);
  }
  for (const list of byHost.values()) {
    if (list.length < 2) continue;
    const [a, b] = list;
    b.wind.pulse.period = a.wind.pulse.period;
    b.wind.pulse.phase = a.wind.pulse.phase + a.wind.pulse.period / 2;
  }
}

/** Bouncer pin chance per wide-deck cell (M13). */
const BUMPER_CHANCE = 0.6;
/** Lateral pin offset from the wide centerline, track units (the wide
 * channel spans ±0.85 — the marble can pass on either side, and the wide
 * walls guarantee recapture; the carom is the spectacle). */
const BUMPER_OFFSET = 0.25;

export function planIslandFurniture(
  rng: Rng,
  plan: SegmentPlan,
  windows: readonly RealizedWindow[],
  /** Assembled placements in TRACK coords — stub hosts are MUTATED
   * (straight → split) exactly like loop-mode stubs. */
  assembled: Placement[],
  /** M21: the zone's BASE weather leans the wind vocabulary — ember
   * storms gust harder and rise; snow muffles the gusts. Threshold
   * multipliers only: draw counts never change. */
  weather: string = "none",
): FurnishedPiece[] {
  const gustMul = weather === "embers" ? 1.4 : weather === "snow" ? 0.7 : 1;
  const updraftMul = weather === "embers" ? 1.4 : 1;
  const out: FurnishedPiece[] = [];
  const gusts: GustEntry[] = [];
  const windowAt = (idx: number): RealizedWindow | undefined =>
    windows.find((w) => idx >= w.start && idx < w.start + w.len);

  // BOUNCER PINS (M13): mushroom bumpers on wide island decks — plinko
  // pins the marble caroms around (high-restitution cylinder proxy;
  // outcome stochastic, recapture guaranteed by the wide channel walls).
  // Wide cells only (the narrow channel has no room to pass a pin) and
  // never inside a planner zone (a boost bleeding into the wide deck must
  // launch true, not pinball).
  for (const crossing of plan.crossings) {
    for (const w of windows) {
      if (
        w.start < crossing.start || w.start >= crossing.start + crossing.len
      ) {
        continue;
      }
      if (w.def.id !== "straight-wide") continue;
      if (
        plan.mods.some((m) => w.start >= m.start && w.start < m.start + m.len)
      ) continue;
      const host = plan.cells[w.start];
      const r = rng.child(`isle/${crossing.id}/bumper/${host.x},${host.z}`);
      if (!r.chance(BUMPER_CHANCE)) continue;
      const side: Dir = r.chance(0.5)
        ? turnLeft(host.dirIn)
        : turnRight(host.dirIn);
      const [dx, dz] = side === 0
        ? [1, 0]
        : side === 1
        ? [0, 1]
        : side === 2
        ? [-1, 0]
        : [0, -1];
      out.push({
        placement: {
          pieceId: "nature-kit:mushroom_red",
          pos: [
            host.x + dx * BUMPER_OFFSET,
            host.baseY + 0.25, // the channel floor — the pin stands IN it
            host.z + dz * BUMPER_OFFSET,
          ],
          rotation: r.int(0, 4) as Dir,
          role: "track",
          bumper: true,
        },
        host: w.start,
      });
    }
  }

  for (const crossing of plan.crossings) {
    for (const sc of crossing.sideCells ?? []) {
      const host = plan.cells[sc.host];
      const win = windowAt(sc.host);
      // Plain straight hosts only — the family carries a bare centered
      // channel (the banner rule's own test); anything else has no
      // usable side port.
      if (!win || win.def.family !== "straight") continue;
      const hp = assembled.find((p) =>
        p.role === "track" && p.pieceId === "straight" &&
        Math.round(p.pos[0]) === host.x &&
        Math.round(p.pos[2]) === host.z &&
        Math.abs(p.pos[1] - host.baseY) < 1e-6
      );
      if (!hp) continue;

      const r = rng.child(
        `isle/${crossing.id}/side/${sc.cell[0]},${sc.cell[1]}`,
      );
      const away: Dir = sc.side === "L"
        ? turnLeft(host.dirIn)
        : turnRight(host.dirIn);
      const [cx, cz] = sc.cell;

      if (r.chance(0.55)) {
        // STUB SIDING: host becomes the 3-way split; the ledge gets a
        // dead-end cap opening back toward the junction.
        hp.pieceId = "split";
        if (sc.side === "R") hp.mirrored = true;
        out.push({
          placement: {
            pieceId: r.pick(CAPS),
            pos: [cx, host.baseY, cz],
            rotation: ((1 - away + 4) % 4) as Dir,
            role: "track",
          },
          host: sc.host,
        });
      } else {
        // FAN: blades face the channel, spinning; boost is kinematic-
        // mode flavor (the endless assist law has its own zones).
        const facing = ((away + 2) % 4) as Dir;
        // Legacy draw order preserved EXACTLY (pick, then float), then
        // the M16 wind-variant draws appended after.
        const crossPiece = r.pick(FANS);
        const radPerSec = 4 + r.float() * 3;
        const variant = r.float();
        const [tx, tz] = DIR_STEP[facing]; // unit step toward the channel
        if (variant < BOOST_FAN_CHANCE) {
          // BOOST CORRIDOR: standing fan aligned WITH the channel,
          // blowing along travel. The scaled disc spans 2 world units —
          // its near edge reaches the rail line — so the wind hint parks
          // the capsule 1 world unit over the channel edge and sinks it
          // toward marble height. ~6.5 u/s² tailwind at the face fading
          // over 5 world units, strongest on the fan's side of the
          // corridor. (Wind hint values are WORLD units.)
          out.push({
            placement: {
              pieceId: r.pick(STANDING_FANS),
              pos: [cx, host.baseY, cz],
              rotation: ((1 - host.dirIn + 4) % 4) as Dir,
              role: "track",
              spin: { axis: [0, 0, 1], radPerSec },
              boost: FAN_BOOST,
              wind: {
                strength: 6.5,
                length: 5,
                radius: 1.3,
                offset: [tx, -0.3, tz],
              },
            },
            host: sc.host,
          });
        } else if (variant < BOOST_FAN_CHANCE + UPDRAFT_FAN_CHANCE * updraftMul) {
          // UPDRAFT COLUMN: table fan flat at the channel lip, blowing
          // straight up — ~9.5 u/s² against gravity 20 (half-weight lift
          // wobble / jump assist, not orbit) for marbles hugging this
          // edge; the hint slides the column over the lip and starts it
          // low enough to catch a rolling marble.
          out.push({
            placement: {
              pieceId: r.pick(TABLE_FANS),
              pos: [
                cx + tx * LIP_OFFSET,
                host.baseY,
                cz + tz * LIP_OFFSET,
              ],
              rotation: ((1 - facing + 4) % 4) as Dir,
              role: "track",
              spin: { axis: [0, 0, 1], radPerSec },
              boost: FAN_BOOST,
              wind: {
                strength: 9.5,
                length: 2.2,
                radius: 0.9,
                offset: [tx * 0.35, -0.5, tz * 0.35],
              },
            },
            host: sc.host,
          });
        } else {
          // M19a draws — appended after the legacy prefix (pick, float,
          // variant), so they exist only in this branch and this cell's
          // child stream; nothing before them moved.
          const extra = r.float();
          if (extra < GUST_CHANCE * gustMul) {
            // GUST WALL: the cross fan keeps its mesh, pose and spin —
            // the upgrade is a pulsed wind field spanning the channel.
            // Coordination (banks / opposed pairs) is a phase-only
            // post-pass over these entries (coordinateGusts).
            const period = GUST_PERIOD_MIN + r.float() * GUST_PERIOD_SPAN;
            const phase = r.float() * period;
            const wind = {
              strength: GUST_STRENGTH,
              length: GUST_LENGTH,
              radius: GUST_RADIUS,
              offset: [tx, -0.3, tz] as [number, number, number],
              pulse: { period, duty: GUST_DUTY, phase },
            };
            out.push({
              placement: {
                pieceId: crossPiece,
                pos: [cx, host.baseY, cz],
                rotation: ((1 - facing + 4) % 4) as Dir,
                role: "track",
                spin: { axis: [0, 0, 1], radPerSec },
                boost: FAN_BOOST,
                wind,
              },
              host: sc.host,
            });
            gusts.push({ wind, host: sc.host, side: sc.side, isle: crossing.id });
          } else if (extra < GUST_CHANCE * gustMul + VORTEX_CHANCE) {
            // VORTEX: table fan at the lip (ledge rule holds — the
            // housing rounds to its cell like the updraft); the swirl
            // column parks over the channel and marbles arc around it.
            out.push({
              placement: {
                pieceId: TABLE_FANS[Math.floor(r.float() * TABLE_FANS.length)],
                pos: [
                  cx + tx * LIP_OFFSET,
                  host.baseY,
                  cz + tz * LIP_OFFSET,
                ],
                rotation: ((1 - facing + 4) % 4) as Dir,
                role: "track",
                spin: { axis: [0, 0, 1], radPerSec },
                boost: FAN_BOOST,
                wind: {
                  strength: VORTEX_STRENGTH,
                  length: VORTEX_HEIGHT,
                  radius: VORTEX_RADIUS,
                  offset: [tx * 1.1, -0.5, tz * 1.1],
                  swirl: true,
                },
              },
              host: sc.host,
            });
          } else {
            // CROSS-WIND (legacy): blades face the channel; the derived
            // capsule crosses it — the client fan field turns this into a
            // lateral gust with no field needed here.
            out.push({
              placement: {
                pieceId: crossPiece,
                pos: [cx, host.baseY, cz],
                rotation: ((1 - facing + 4) % 4) as Dir,
                role: "track",
                spin: { axis: [0, 0, 1], radPerSec },
                boost: FAN_BOOST,
              },
              host: sc.host,
            });
          }
        }
      }
    }
  }
  coordinateGusts(gusts);
  return out;
}
